import React from "react";

import { ContentModule } from "utils/orm";

export interface TocModuleProps {
    style: string;
    entities: ContentModule[]; // eslint-disable-line no-use-before-define
    inToc?: boolean;
    heading?: string;
    type: "tocGenerator";
    id?: number;
}

export interface TocEntities {
    heading: string;
    id: number;
}

interface ListProps {
    style: string;
}

const TOCList: React.FC<ListProps> = (props) => {
    const { children, style } = props;
    return style === "numeric"
        ? <ol>{children}</ol>
        : <ul>{children}</ul>;
};

/**
 * Return only the heading and the ID
 */
const createEntry = (module: any) => {
    const {
        heading, id, inToc, type
    } = module;
    return inToc ? {
        heading, id, inToc, type
    } : null;
};

/**
 * Checks to see if we have ny items are marked as being part of the TOC.
 */
const hasTocItems = (modules: ContentModule[]) => !!modules && !!modules.filter((module: any) => module && module.inToc && module.heading).length;

/**
 * Checks to see if we have a TOC generator.
 */
const hasTocGenerator = (modules: ContentModule[]) => !!modules.filter((module) => module && module.type === "tocGenerator").length;

/**
 * Checks for tocItems and generators.
 */
export const hasToc = (modules: ContentModule[]) => hasTocItems(modules) && hasTocGenerator(modules);

/**
 * Get all headings and IDs that are part of the TOC or an empty array.
 */
export const getTOCEntries = (modules: ContentModule[]) => (!!modules && hasToc(modules)
    ? modules.filter((module: any) => module && module.inToc && module.heading).map(createEntry)
    : []);

interface IListITem {
    cleanHeading: string;
    id: number;
    index: number;
    type: string;
}

/**
 * Generate the list item either a paragraph for dividers or an LI.
 */
const ListItem: React.FC<IListITem> = (props) => {
    const {
        cleanHeading, id, index, type
    } = props;
    return type === "divider" ? (
        <p key={index}>
            <a href={`#${id}`}>{cleanHeading}</a>
        </p>
    ) : (
        <li key={index}>
            <a href={`#${id}`}>{cleanHeading}</a>
        </li>
    );
};

/**
 * Generates a Table of contents. With jump links to the content.
 */
export const TocModule: React.FC<TocModuleProps> = (props) => {
    const { style, entities } = props;
    if (!entities) {
        return null;
    }
    const listItems = entities.map((item: any, index: number) => {
        const {
            heading, id, inToc, type
        } = item;

        // We strip out the markup for clean text in the bullet. We do not want nested anchors.
        const cleanHeading = inToc && heading && heading.replace(/<[^>]*>?/gm, "");
        return !!cleanHeading && (
            <ListItem {...{
                cleanHeading, id, index, type
            }}
            />
        );
    });

    return <TOCList style={style}>{listItems}</TOCList>;
};
